import * as React from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Fade,
  useTheme,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import GraystoneGroupLogo from './GraystoneGroupLogo';

const Navbar = () => {
  const theme = useTheme();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const menuItems = [
    { text: 'HOME', path: '/' },
    { text: 'GRAYSTONE', path: '/commercial' },
    { text: 'GRAYSTONE FEDERAL', path: '/federal' },
    { text: 'CONTACT', path: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <AppBar 
      position="sticky" 
      sx={{
        borderBottom: `1px solid ${theme.palette.primary.light}`,
        backgroundColor: theme.palette.primary.main,
        px: 2,
      }}
    >
      <Toolbar disableGutters sx={{ minHeight: { xs: 52, sm: 80 }, justifyContent: 'space-between', width: '100%' }}>
        {/* Company Logo (replace name with SVG) */}
        <Box
          key={'/'}
          component={Link}
          to={'/'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            l: 4,
            minWidth: { xs: 40 },
            minHeight: { xs: 30, sm: 38, md: 46 },
          }}
        >
          <Box
            sx={{
              marginRight: {xs: -24, sm: -28},
              mt: 1,
              minWidth: 200, 
              maxWidth: {xs: 200, sm: 240},
            }}
          >
          <GraystoneGroupLogo />
        </Box>
        </Box>

        {/* Desktop Navigation - Show on md and up */}
        <Box 
          sx={{ 
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1,
            ml: 'auto'
          }}
        >
          {menuItems.map((item) => (
            <Button
              key={item.path}
              color="inherit"
              component={Link}
              to={item.path}
              sx={{
                position: 'relative',
                px: 2,
                py: 1,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: '2px',
                  backgroundColor: theme.palette.secondary.main,
                  transform: isActive(item.path) ? 'scaleX(1)' : 'scaleX(0)',
                  transition: theme.transitions.create(['transform'], {
                    duration: theme.transitions.duration.shorter,
                  }),
                },
                '&:hover::after': {
                  transform: 'scaleX(1)',
                },
                ...(isActive(item.path) && {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                }),
              }}
            >
              {item.text}
            </Button>
          ))}
        </Box>

        {/* Mobile Menu Button - Show on xs and sm */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, ml: 'auto', mr: 2 }}>
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleClick}
            sx={{
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: theme.transitions.create(['transform'], {
                duration: theme.transitions.duration.shorter,
              }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            TransitionComponent={Fade}
            sx={{
              '& .MuiPaper-root': {
                width: '100%',
                maxWidth: '300px',
                mt: 1,
                background: theme.palette.primary.main,
                backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.1) 100%)',
              },
            }}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.path}
                onClick={handleClose}
                component={Link}
                to={item.path}
                selected={isActive(item.path)}
                sx={{ 
                  py: 1.5,
                  justifyContent: 'center',
                  color: 'white',
                  borderBottom: `2px solid ${isActive(item.path) ? theme.palette.secondary.main : 'transparent'}`,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderBottom: `2px solid ${theme.palette.secondary.light}`,
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                {item.text}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 